import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoForm from '../exo/ExoForm';
import mapServerErrors from '../form/mapServerErrors';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Loader from '../special/Loader';
import { useState, useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';
import moment from 'moment';

const NewEmptyProject = ({ onClose, onCreate, startDataCollection }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [nrBlocked, setNrBlocked] = useState(false);
  // a custom project number makes the prefix irrelevant
  const [hasCustomNr, setHasCustomNr] = useState(
    Boolean(startDataCollection?.nr)
  );
  // backend validation errors from the last submit attempt
  const [serverErrors, setServerErrors] = useState(null);
  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const fields = [
    {
      label: t('Image'),
      type: 'image',
      key: 'image',
      colspan: 2,
    },

    {
      label: t('Project Number'),
      type: 'projectNr',
      key: 'nr',
      colspan: 2,
      onCheckStateChange: setNrBlocked,
    },
    {
      label: t('Project Name'),
      type: 'text',
      key: 'name',
      colspan: 2,
    },
    {
      label: t('Description'),
      type: 'richText',
      key: 'description',
      colspan: 2,
    },
    {
      label: t('Category'),
      key: 'categories',
      type: 'projectCategory',
      colspan: 2,
    },
    {
      label: `${t('Prefix')}`,
      type: 'text',
      key: 'prefix',
      disabled: hasCustomNr,
    },
    {
      label: `${t('Customer Identifier')}`,
      type: 'text',
      key: 'externalNr',
    },
    {
      label: t('Preset'),
      type: 'projectPreset',
      key: 'preset',
      colspan: 2,
      hidden: Boolean(startDataCollection?.id),
    },
    {
      label: t('Network Storage'),
      type: 'networkStorage',
      key: 'networkStorage',
    },

    {
      label: t('Supervisor'),
      type: 'user',
      key: 'supervisor',
    },
    {
      label: t('Address'),
      type: 'address',
      key: 'address',
      colspan: 2,
      suggestionsArr: [
        {
          key: 'participants',
          isArray: true,
          type: 'participant',
        },
        {
          key: 'client',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Project Participants'),
      key: 'participants',
      type: 'participantsAutoComplete',
      searchParams: ['person_leads', 'company_leads'],
      colspan: 2,
      suggestionsArr: [
        {
          key: 'client',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Client'),
      type: 'client',
      key: 'client',
      colspan: 2,
    },
    {
      label: t('Start Date'),
      type: 'date',
      value: 'today',
      key: 'startDate',
    },

    {
      label: `${t('Deadline')}`,
      type: 'date',
      key: 'deadline',
    },
  ];
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('Project Name is required')),
    supervisor: Yup.object().shape({
      id: Yup.string().required(t('Supervisor is required')),
    }),
    startDate: Yup.string().required(t('Start Date is required')),
    clientType: Yup.string().required(t('Client Type is required')),
    client: Yup.mixed().when('clientType', {
      is: (value) => value === 'contact' || value === 'company',
      then: (schema) =>
        Yup.object().shape({
          id: Yup.string().required(t('Supervisor is required')),
        }),
      otherwise: (schema) => Yup.object().nullable(),
    }),
    networkStorage: Yup.string().required(t('Is required')),
    preset: startDataCollection?.id
      ? Yup.mixed().nullable()
      : Yup.object().required(t('Preset is required')),
  });
  const config = useConfig();

  // Track if we've initialized default data to prevent re-initialization
  const initializedRef = useRef(false);
  const [defaultDataCollection, setDefaultDataCollection] = useState(() => {
    if (startDataCollection) return startDataCollection;
    return {
      status: 0,
      clientType: 'internal',
      networkStorage: undefined, // Will be set when config loads
      supervisor: { id: userId },
      startDate: moment().format('YYYY-MM-DD'),
    };
  });

  // Set networkStorage default once when config becomes available
  useEffect(() => {
    if (initializedRef.current) return;
    if (!config?.project?.defaultNetworkStorage) return;
    if (startDataCollection) return; // Don't override if editing

    initializedRef.current = true;
    setDefaultDataCollection((prev) => ({
      ...prev,
      networkStorage: config.project.defaultNetworkStorage,
    }));
  }, [config, startDataCollection]);

  function handleSubmit(data) {
    setServerErrors(null);
    const isUpdate = Boolean(startDataCollection && startDataCollection.id);

    // If "No Preset" is selected then it is an empty object or has an id of null, set it to null before api call
    if (data.preset && data.preset.id == null) {
      data = { ...data, preset: null };
    }

    // no custom project number -> let the backend auto generate one
    if (!data.nr) {
      const { nr, ...rest } = data;
      data = rest;
    }

    DBRequest({
      config,
      path: isUpdate ? `projects/${startDataCollection.id}` : `projects`,
      method: isUpdate ? API_PATCH(data) : API_POST({ ...data, status: 0 }),
      onResponse: handleCreate,
      onError: handleServerError,
      onLoading: setIsLoading,
    });
  }

  function handleServerError(error, status) {
    if (status === 422 && error?.errors) {
      setServerErrors(mapServerErrors(error.errors, fields));
    } else {
      setServerErrors({
        fieldErrors: {},
        messages: [{ message: error?.message || t('Something went wrong') }],
      });
    }
  }

  function handleCreate(data) {
    onClose();
    onCreate(data.data);
  }

  return (
    <Box
      className="p-2 mx-auto"
      sx={{
        width: '620px',
        maxWidth: '100%',
      }}
    >
      <Loader active={isLoading} />
      <ExoForm
        header={
          startDataCollection ? t('Update Project') : t('New Empty Project')
        }
        fields={fields}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        onCancel={onClose}
        onChange={(data) => setHasCustomNr(Boolean(data.nr))}
        startDataCollection={defaultDataCollection}
        className="grid grid-cols-2 gap-4 py-4 px-3"
        noDifferences
        isNotValid={nrBlocked}
        externalErrors={serverErrors?.fieldErrors}
      />
      {serverErrors && serverErrors.messages.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>{t('Saving failed')}</AlertTitle>
          {serverErrors.messages.map((item, index) => (
            <Typography key={index} variant="body2">
              {item.label ? `${item.label}: ${item.message}` : item.message}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export default NewEmptyProject;
